import { useEffect, useCallback } from "react";
import { useNavigate, useMatch } from "react-router";

import { useApi } from "../../api";
import { useAuth } from "./useAuth";
import { isEmailVerified } from "./useIsSsoUser";
import {
  useAppSelector,
  useAppDispatch,
  fetchCompanies,
  assignDefaultCompanyRole,
} from "../../store";
import { useIsSelfManagedAuthRoute } from "./useIsSelfManagedAuthRoute";

export const useAuthRedirection = (isAuthRoute: boolean) => {
  const { companies, status: companiesStatus } = useAppSelector(
    (state) => state.companies,
  );
  const navigate = useNavigate();
  const { user } = useAuth();
  const { getApi } = useApi();
  const dispatch = useAppDispatch();
  const isVerify = !!useMatch("/auth/verify");
  const isOnboarding = !!useMatch("/auth/onboarding");
  // The invited/reset-password user already has a company assigned server-side before
  // they've set a password or completed their profile — without this guard they'd get
  // bounced to "/" by the last branch below as soon as companies load, mid-flow.
  const isSelfManagedAuthRoute = useIsSelfManagedAuthRoute();
  const defaultCompanyRoleStatus = useAppSelector(
    (state) => state.user.defaultCompanyRoleStatus,
  );

  // The single place the default company role is requested: any verified user,
  // on any sign-in path (email, SSO, verified on another device), once per
  // session (see the thunk). Membership in the company mapped to the email
  // domain doesn't depend on other company assignments; the BFF is a no-op
  // when the user already has a role there, and won't re-add a user who was
  // explicitly removed from that company.
  const updateCompanies = useCallback(async () => {
    if (!user) return;
    // Verification may have happened in another tab or device since this
    // session's user object was loaded.
    if (!isEmailVerified(user)) {
      await user.reload().catch(console.warn);
    }
    const api = await getApi();
    const result = await dispatch(fetchCompanies(api));
    if (isEmailVerified(user) && fetchCompanies.fulfilled.match(result)) {
      dispatch(assignDefaultCompanyRole());
    }
  }, [user, dispatch, getApi]);

  useEffect(() => {
    if (!user) return;

    updateCompanies();
  }, [user, updateCompanies]);

  useEffect(() => {
    if (!companies || !user || companiesStatus !== "fulfilled") {
      return;
    }

    const emailVerified = isEmailVerified(user);

    // A verified user without companies may be about to get one from the
    // default role assignment — wait for it rather than bouncing to
    // /auth/noCompany. Anyone with companies is redirected right away while
    // the assignment (if any) finishes in the background.
    const defaultRolePending =
      defaultCompanyRoleStatus === "idle" ||
      defaultCompanyRoleStatus === "loading";
    if (companies.length === 0 && emailVerified && defaultRolePending) {
      return;
    }

    if (isSelfManagedAuthRoute) {
      return;
    }

    if (isVerify && !emailVerified) {
      return;
    }

    if (!emailVerified) {
      navigate("/auth/verify");
    } else if (companies.length === 0 && !isOnboarding) {
      navigate("/auth/noCompany");
    } else if (isAuthRoute && companies.length > 0) {
      navigate("/");
    }
  }, [
    companies,
    navigate,
    user,
    isVerify,
    companiesStatus,
    defaultCompanyRoleStatus,
    isAuthRoute,
    isOnboarding,
    isSelfManagedAuthRoute,
  ]);
};
